import { useState, useEffect } from 'react';
import { Button, Spinner, TextControl, ToggleControl, SelectControl } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import Header from '../../components/layout/Header';
import Body from '../../components/layout/Body';
import Tabs from '../../components/layout/Tabs';
import { useNotice } from '../../contexts/NoticeContext';
import { useURLParams } from '../../contexts/ParamsContext';
import { fetchPostType, savePostType } from '../../utils/api';

// Utility function to sanitize slugs and allow / and {term}/{date}
const generateMetaKey = (label) => {
    return label
        .toLowerCase()
        .replace(/[^a-z0-9_\/\{\}]+/g, '')
        .replace(/^\/+/, '');
};

// Validate only allowed placeholders
const allowedPlaceholders = ['{term}', '{date}', '{post}'];
// Validate any placeholders in a slug
const validatePlaceholders = (slug) => {
    // Match all {placeholder} in the string
    const matches = slug.match(/\{[a-zA-Z0-9_-]+\}/g) || [];
    // Every matched placeholder must be in the allowed list
    return matches.every(ph => allowedPlaceholders.includes(ph.toLowerCase()));
};

const PostTypeEdit = () => {
    const [isSaving, setIsSaving] = useState(false);
    const { params, updateUrlParams } = useURLParams();
    const { showNotice } = useNotice();

    const [postTypeData, setPostTypeData] = useState({
        id: null,
        name: '',
        singular_name: '',
        slug: '',
        public: true,
        hierarchical: false,
        labels: { menu_name: '' },
        visibility: {
            show_in_ui: true,
            show_in_menu: true,
            menu_position: 25,
            menu_icon: 'dashicons-admin-post',
            show_in_rest: true,
            publicly_queryable: true,
            exclude_from_search: false,
        },
        supports: ['title', 'editor', 'custom-fields'],
        rewrite_slug: '',
    });

    const [rewriteSlugType, setRewriteSlugType] = useState('default');
    const [customRewriteSlug, setCustomRewriteSlug] = useState('');

    const supportOptions = [
        { key: 'title', label: __('Title', 'voxycure-framework') },
        { key: 'editor', label: __('Editor', 'voxycure-framework') },
        { key: 'thumbnail', label: __('Featured Image', 'voxycure-framework') },
        { key: 'excerpt', label: __('Excerpt', 'voxycure-framework') },
        { key: 'comments', label: __('Comments', 'voxycure-framework') },
        { key: 'custom-fields', label: __('Custom Fields', 'voxycure-framework') },
        { key: 'revisions', label: __('Revisions', 'voxycure-framework') },
        { key: 'author', label: __('Author', 'voxycure-framework') },
        { key: 'page-attributes', label: __('Page Attributes', 'voxycure-framework') },
        { key: 'post-formats', label: __('Post Formats', 'voxycure-framework') },
    ];

    useEffect(() => {
        if (!params.id) return;

        fetchPostType(params.id)
            .then(data => {
                setPostTypeData({
                    id: data.id || null,
                    name: data.name || '',
                    singular_name: data.singular_name || '',
                    slug: data.slug || '',
                    public: data.public === '1',
                    hierarchical: data.hierarchical === '1',
                    labels: {
                        menu_name: data.menu_name || '',
                    },
                    visibility: {
                        show_in_ui: data.show_in_ui === '1',
                        show_in_menu: data.show_in_menu === '1',
                        menu_position: Number(data.menu_position) || 25,
                        menu_icon: data.menu_icon || 'dashicons-admin-post',
                        show_in_rest: data.show_in_rest === '1',
                        publicly_queryable: data.publicly_queryable !== '0',
                        exclude_from_search: data.exclude_from_search === '1',
                    },
                    supports: data.supports || ['title', 'editor'],
                    rewrite_slug: data.rewrite_slug || '',
                });

                // Determine rewriteSlugType based on fetched data
                const generated = generateMetaKey(data.slug || '');
                if (data.rewrite_slug === 'prevent') {
                    setRewriteSlugType('prevent');
                } else if (data.rewrite_slug && data.rewrite_slug !== generated) {
                    setRewriteSlugType('custom');
                    setCustomRewriteSlug(data.rewrite_slug);
                } else {
                    setRewriteSlugType('default');
                }
            })
            .catch(error => {
                showNotice(
                    error?.message || __('Error fetching post type.', 'voxycure-framework'),
                    'error'
                );
            });
    }, [params.id]);

    const handleSave = () => {
        setIsSaving(true);

        const finalRewriteSlug =
            rewriteSlugType === 'default'
                ? generateMetaKey(postTypeData.slug || '')
                : rewriteSlugType === 'prevent'
                    ? 'prevent'
                    : customRewriteSlug;

        if (rewriteSlugType === 'custom' && !validatePlaceholders(finalRewriteSlug)) {
            showNotice(
                __('Invalid placeholder used. Only {term} and {date} are allowed.', 'voxycure-framework'),
                'error'
            );
            setIsSaving(false);
            return;
        }

        savePostType({
            ...postTypeData,
            rewrite_slug: finalRewriteSlug,
        })
            .then(savedData => {
                setPostTypeData(prev => ({ ...prev, id: savedData?.data?.id }));
                updateUrlParams({
                    view: 'edit-post-type',
                    id: savedData?.data?.id,
                });

                setTimeout(() => {
                    showNotice(
                        savedData?.message || __('Post Type saved successfully.', 'voxycure-framework'),
                        'success'
                    );
                }, 50);
            })
            .catch(error => {
                showNotice(
                    error?.message || __('Error saving post type.', 'voxycure-framework'),
                    'error'
                );
            })
            .finally(() => setIsSaving(false));
    };

    const tabList = [
        { name: 'post-type-settings', title: __('Post Type Settings', 'voxycure-framework'), className: 'post-type-settings-tab' },
        { name: 'visibility', title: __('Visibility', 'voxycure-framework'), className: 'visibility-tab' },
        { name: 'supports', title: __('Supports', 'voxycure-framework'), className: 'supports-tab' },
    ];

    return (
        <>
            <Header>
                <h2 className="voxycure-editor__header-title">
                    {postTypeData.id
                        ? __('Edit Post Type', 'voxycure-framework')
                        : __('Add New Post Type', 'voxycure-framework')}
                </h2>
                <Button
                    className="components-button is-primary voxycure-button"
                    onClick={handleSave}
                    disabled={isSaving}
                >
                    {isSaving ? (
                        <>
                            <Spinner style={{ verticalAlign: 'middle', marginRight: 6 }} size={16} />
                            {__('Saving...', 'voxycure-framework')}
                        </>
                    ) : (
                        __('Save', 'voxycure-framework')
                    )}
                </Button>
            </Header>

            <Body>
                <Tabs
                    tabs={tabList}
                    renderTab={(tab) => {
                        switch (tab.name) {
                            case 'post-type-settings':
                                return (
                                    <>
                                        <TextControl
                                            label={__('Plural Label *', 'voxycure-framework')}
                                            value={postTypeData.name || ''}
                                            onChange={value =>
                                                setPostTypeData({ ...postTypeData, name: value })
                                            }
                                            required
                                        />
                                        <TextControl
                                            label={__('Singular Label *', 'voxycure-framework')}
                                            value={postTypeData.singular_name || ''}
                                            onChange={value =>
                                                setPostTypeData({ ...postTypeData, singular_name: value })
                                            }
                                            required
                                        />
                                        <TextControl
                                            label={__('Post Type Slug *', 'voxycure-framework')}
                                            value={postTypeData.slug || ''}
                                            onChange={value => {
                                                setPostTypeData({ ...postTypeData, slug: value });
                                                if (rewriteSlugType === 'default') {
                                                    setPostTypeData(prev => ({
                                                        ...prev,
                                                        rewrite_slug: generateMetaKey(value),
                                                    }));
                                                }
                                            }}
                                            required
                                        />
                                        <TextControl
                                            label={__('Menu Name', 'voxycure-framework')}
                                            value={postTypeData.labels?.menu_name || ''}
                                            onChange={value =>
                                                setPostTypeData({
                                                    ...postTypeData,
                                                    labels: { ...postTypeData.labels, menu_name: value },
                                                })
                                            }
                                        />

                                        <SelectControl
                                            label={__('Rewrite Slug', 'voxycure-framework')}
                                            value={rewriteSlugType}
                                            options={[
                                                { label: __('Post Type key (default)', 'voxycure-framework'), value: 'default' },
                                                { label: __('No Permalink (prevent URL rewriting)', 'voxycure-framework'), value: 'prevent' },
                                                { label: __('Custom', 'voxycure-framework'), value: 'custom' },
                                            ]}
                                            onChange={(value) => {
                                                setRewriteSlugType(value);

                                                if (value === 'default') {
                                                    const autoSlug = generateMetaKey(postTypeData.name || '');
                                                    setPostTypeData(prev => ({
                                                        ...prev,
                                                        rewrite_slug: autoSlug,
                                                    }));
                                                } else if (value === 'prevent') {
                                                    setPostTypeData(prev => ({
                                                        ...prev,
                                                        rewrite_slug: 'prevent',
                                                    }));
                                                }
                                            }}
                                        />

                                        {rewriteSlugType === 'custom' && (
                                            <TextControl
                                                label={__('Custom Rewrite Slug', 'voxycure-framework')}
                                                value={customRewriteSlug}
                                                onChange={(value) => {
                                                    const sanitized = generateMetaKey(value);
                                                    setCustomRewriteSlug(sanitized);
                                                    setPostTypeData(prev => ({
                                                        ...prev,
                                                        rewrite_slug: sanitized,
                                                    }));
                                                }}
                                                help={__('You can use / and placeholders {term}, {date}.', 'voxycure-framework')}
                                            />
                                        )}
                                    </>
                                );

                            case 'visibility':
                                return (
                                    <>
                                        <ToggleControl
                                            label={__('Show In UI', 'voxycure-framework')}
                                            checked={!!postTypeData.visibility?.show_in_ui}
                                            onChange={value =>
                                                setPostTypeData({
                                                    ...postTypeData,
                                                    visibility: { ...postTypeData.visibility, show_in_ui: value },
                                                })
                                            }
                                        />
                                        <ToggleControl
                                            label={__('Show In Admin Menu', 'voxycure-framework')}
                                            checked={!!postTypeData.visibility?.show_in_menu}
                                            onChange={value =>
                                                setPostTypeData({
                                                    ...postTypeData,
                                                    visibility: { ...postTypeData.visibility, show_in_menu: value },
                                                })
                                            }
                                        />
                                        <TextControl
                                            label={__('Menu Position', 'voxycure-framework')}
                                            value={postTypeData.visibility?.menu_position || ''}
                                            onChange={value =>
                                                setPostTypeData({
                                                    ...postTypeData,
                                                    visibility: { ...postTypeData.visibility, menu_position: value },
                                                })
                                            }
                                        />
                                        <TextControl
                                            label={__('Menu Icon', 'voxycure-framework')}
                                            value={postTypeData.visibility?.menu_icon || ''}
                                            onChange={value =>
                                                setPostTypeData({
                                                    ...postTypeData,
                                                    visibility: { ...postTypeData.visibility, menu_icon: value },
                                                })
                                            }
                                        />
                                        <ToggleControl
                                            label={__('Show In REST API', 'voxycure-framework')}
                                            checked={!!postTypeData.visibility?.show_in_rest}
                                            onChange={value =>
                                                setPostTypeData({
                                                    ...postTypeData,
                                                    visibility: { ...postTypeData.visibility, show_in_rest: value },
                                                })
                                            }
                                        />
                                        <ToggleControl
                                            label={__('Publicly Queryable', 'voxycure-framework')}
                                            checked={!!postTypeData.visibility?.publicly_queryable}
                                            onChange={value =>
                                                setPostTypeData({
                                                    ...postTypeData,
                                                    visibility: {
                                                        ...postTypeData.visibility,
                                                        publicly_queryable: value,
                                                    },
                                                })
                                            }
                                        />
                                        <ToggleControl
                                            label={__('Exclude From Search', 'voxycure-framework')}
                                            checked={!!postTypeData.visibility?.exclude_from_search}
                                            onChange={value =>
                                                setPostTypeData({
                                                    ...postTypeData,
                                                    visibility: {
                                                        ...postTypeData.visibility,
                                                        exclude_from_search: value,
                                                    },
                                                })
                                            }
                                        />
                                        <ToggleControl
                                            label={__('Public', 'voxycure-framework')}
                                            checked={!!postTypeData.public}
                                            onChange={value =>
                                                setPostTypeData({ ...postTypeData, public: value })
                                            }
                                        />
                                        <ToggleControl
                                            label={__('Hierarchical', 'voxycure-framework')}
                                            checked={!!postTypeData.hierarchical}
                                            onChange={value =>
                                                setPostTypeData({ ...postTypeData, hierarchical: value })
                                            }
                                        />
                                    </>
                                );

                            case 'supports':
                                return (
                                    <>
                                        {supportOptions.map(option => (
                                            <ToggleControl
                                                key={option.key}
                                                label={option.label}
                                                checked={postTypeData.supports.includes(option.key)}
                                                onChange={isChecked => {
                                                    setPostTypeData(prev => {
                                                        const supports = isChecked
                                                            ? [...prev.supports, option.key]
                                                            : prev.supports.filter(item => item !== option.key);
                                                        return { ...prev, supports };
                                                    });
                                                }}
                                            />
                                        ))}
                                    </>
                                );

                            default:
                                return null;
                        }
                    }}
                />
            </Body>
        </>
    );
};

export default PostTypeEdit;
